/**
 * Theme Lab — user-composed accent, radius, and density applied as
 * semantic CSS custom properties on the document root.
 */
const KEY = "gb-theme-lab";

export interface ThemeLabConfig {
  hue: number;        // 0-360
  saturation: number; // 0-100
  lightness: number;  // 30-70
  radius: number;     // rem
  glow: boolean;
}

export const DEFAULT_THEME_LAB: ThemeLabConfig = {
  hue: 262,
  saturation: 83,
  lightness: 58,
  radius: 0.75,
  glow: true,
};

export const THEME_PRESETS: { name: string; config: ThemeLabConfig }[] = [
  { name: "Genie Violet", config: DEFAULT_THEME_LAB },
  { name: "Cyber Teal", config: { hue: 174, saturation: 78, lightness: 45, radius: 0.5, glow: true } },
  { name: "Solar Amber", config: { hue: 38, saturation: 92, lightness: 55, radius: 1, glow: true } },
  { name: "Noir Steel", config: { hue: 220, saturation: 14, lightness: 62, radius: 0.25, glow: false } },
  { name: "Rose Circuit", config: { hue: 340, saturation: 80, lightness: 60, radius: 1.25, glow: true } },
];

export function loadThemeLab(): ThemeLabConfig {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return DEFAULT_THEME_LAB;
    return { ...DEFAULT_THEME_LAB, ...JSON.parse(raw) };
  } catch {
    return DEFAULT_THEME_LAB;
  }
}

export function saveThemeLab(config: ThemeLabConfig): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(config));
  } catch {
    // ignore
  }
}

export function clearThemeLab(): void {
  try {
    localStorage.removeItem(KEY);
  } catch {
    // ignore
  }
  const root = document.documentElement;
  ["--primary", "--ring", "--accent", "--radius"].forEach((prop) => root.style.removeProperty(prop));
  root.removeAttribute("data-theme-lab-glow");
}

export function applyThemeLab(config: ThemeLabConfig): void {
  const root = document.documentElement;
  const primary = `${config.hue} ${config.saturation}% ${config.lightness}%`;
  root.style.setProperty("--primary", primary);
  root.style.setProperty("--ring", primary);
  root.style.setProperty("--accent", `${config.hue} ${Math.max(20, config.saturation - 40)}% ${Math.min(92, config.lightness + 25)}%`);
  root.style.setProperty("--radius", `${config.radius}rem`);
  root.setAttribute("data-theme-lab-glow", config.glow ? "on" : "off");
}

/** Compact shareable code, e.g. "262-83-58-75-1". */
export function encodeThemeLab(config: ThemeLabConfig): string {
  return [
    Math.round(config.hue),
    Math.round(config.saturation),
    Math.round(config.lightness),
    Math.round(config.radius * 100),
    config.glow ? 1 : 0,
  ].join("-");
}

export function decodeThemeLab(code: string): ThemeLabConfig | null {
  const parts = code.trim().split("-").map(Number);
  if (parts.length !== 5 || parts.some((n) => Number.isNaN(n))) return null;
  const [hue, saturation, lightness, radius, glow] = parts;
  if (hue < 0 || hue > 360 || saturation < 0 || saturation > 100 || lightness < 0 || lightness > 100) {
    return null;
  }
  return {
    hue,
    saturation,
    lightness,
    radius: Math.min(2, Math.max(0, radius / 100)),
    glow: glow === 1,
  };
}

/** Call once at app start so a saved theme survives reloads. */
export function initThemeLab(): void {
  try {
    if (!localStorage.getItem(KEY)) return;
  } catch {
    return;
  }
  applyThemeLab(loadThemeLab());
}
